import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles } from 'lucide-react';
import { useEffect, useLayoutEffect, useState, useRef, lazy, Suspense } from 'react';
import { HOME_SCROLL_KEY, clearShopScrollState } from '@/utils/scrollStorage';
import { Button } from '@/components/ui/button';
import { ProductCard } from '@/components/ProductCard';
import { useLanguage } from '@/contexts/LanguageContext';
import { useServerHomepageData } from '@/hooks/useServerProducts';
import SEOHead from '@/components/SEOHead';
import brandStoryImage from '@/assets/brand-story.jpg';

// Lazy load the AI assistant - only needed when user clicks
const ProductGuideAssistant = lazy(() => 
  import('@/components/ProductGuideAssistant').then(m => ({ default: m.ProductGuideAssistant }))
);

const Home = () => {
  const { t, language } = useLanguage();
  const [isAssistantOpen, setIsAssistantOpen] = useState(false);
  const scrollRestoredRef = useRef(false);

  // Server-optimized query - only fetches featured + new arrivals with LIMIT
  const { data, isLoading: loading } = useServerHomepageData();
  
  const featuredProducts = data?.featuredProducts || [];
  const newArrivals = data?.newArrivals || [];
  const heroContent = data?.heroContent;

  // Save scroll position with throttling to reduce forced reflow
  useEffect(() => {
    let ticking = false;
    let lastScrollY = 0;

    const handleScroll = () => {
      lastScrollY = window.scrollY;
      
      if (!ticking) {
        requestAnimationFrame(() => {
          try {
            sessionStorage.setItem(HOME_SCROLL_KEY, String(lastScrollY));
          } catch {
            // Ignore storage errors
          }
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Restore scroll position after content loads
  useLayoutEffect(() => {
    if (loading || scrollRestoredRef.current) return;
    
    scrollRestoredRef.current = true;
    
    try {
      const savedY = sessionStorage.getItem(HOME_SCROLL_KEY);
      if (savedY) {
        requestAnimationFrame(() => {
          window.scrollTo(0, Number(savedY));
        });
      }
    } catch {
      // Ignore storage errors
    }
  }, [loading]);

  return (
    <div className="min-h-screen">
      {/* SEO Meta Tags - Brand-focused for homepage */}
      <SEOHead
        title="Luxury Organic Beauty from Lebanon"
        description="Discover 100% organic Lebanese beauty products from Khan El Saboun. Handcrafted soaps, oils, serums with 600+ years of traditional craftsmanship. Shop luxury natural skincare online."
        canonicalPath="/"
      />
      {/* Hero Section - Optimized for mobile LCP */}
      <section className="hero-section relative">
        <div className="absolute inset-0">
          <img
            src="/images/hero-main.jpg"
            alt="Luxury organic soap products from Lebanon"
            className="w-full h-full object-cover"
            fetchPriority="high"
            loading="eager"
            decoding="sync"
            width="1600"
            height="900"
            sizes="100vw"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 to-black/20" />
        </div>
        <div className="relative z-10 section-container text-center text-white">
          {/* Static fallback text renders immediately, data replaces it */}
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-serif font-light mb-6 animate-fade-in">
            {heroContent ? (
              language === 'sv' ? heroContent.hero_title_sv :
              language === 'ar' ? heroContent.hero_title_ar :
              heroContent.hero_title_en
            ) : t('discoverNature')}
          </h1>
          <p className="text-lg md:text-xl mb-8 max-w-2xl mx-auto animate-fade-in-delay">
            {heroContent ? (
              language === 'sv' ? heroContent.hero_subtitle_sv :
              language === 'ar' ? heroContent.hero_subtitle_ar :
              heroContent.hero_subtitle_en
            ) : t('heroSubtitle')}
          </p>
          <Link to="/shop" onClick={clearShopScrollState}>
            <Button className="btn-primary text-lg px-10 py-6 animate-scale-in">
              {t('shopNow')}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Brand Story Section */}
      <section className="section-container">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div className="space-y-6 animate-fade-in">
            <h2 className="text-3xl md:text-4xl font-serif font-medium text-foreground">
              {t('brandStoryTitle')}
            </h2>
            <p className="text-muted-foreground leading-relaxed text-left md:text-justify">
              {t('brandStory')}
            </p>
            <Link to="/about">
              <Button variant="outline" className="smooth-transition">
                {t('about')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
          <div className="relative animate-fade-in-delay aspect-[4/5] md:aspect-auto">
            <img
              src={brandStoryImage}
              alt="Khan El Saboun traditional Lebanese soap-making heritage - 600 years of organic craftsmanship"
              className="rounded-lg shadow-luxury w-full h-full md:h-[500px] object-cover"
              width="600"
              height="500"
              loading="lazy"
              decoding="async"
              fetchPriority="low"
              sizes="(max-width: 768px) 100vw, 50vw"
            />
          </div>
        </div>
      </section>

      {/* AI Product Guide Section */}
      <section className="section-container">
        <div className="max-w-3xl mx-auto">
          <div className="bg-gradient-to-br from-primary/5 via-secondary/5 to-accent/5 rounded-2xl p-8 md:p-12 border border-primary/10 shadow-soft">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center shadow-lg">
                  <Sparkles className="h-8 w-8 text-white" />
                </div>
              </div>
              <div className="flex-1 text-center md:text-left space-y-3">
                <h2 className="text-2xl md:text-3xl font-serif font-medium text-foreground">
                  {t('smartProductGuide')}
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  {t('smartProductGuideText')}
                </p>
              </div>
              <div className="flex-shrink-0">
                <Button 
                  onClick={() => setIsAssistantOpen(true)}
                  className="btn-primary text-base px-8 py-6 shadow-lg hover:shadow-xl transition-all"
                >
                  {t('startSmartProductGuide')}
                  <Sparkles className="ml-2 h-5 w-5" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products Section */}
      <section className="section-container bg-sand">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-serif font-medium text-foreground mb-4">
            {t('featuredProducts')}
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            {t('organicBenefits')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {loading ? (
            <p className="text-center col-span-full">Loading...</p>
          ) : featuredProducts.length === 0 ? (
            <p className="text-center col-span-full text-muted-foreground">No featured products yet</p>
          ) : (
            featuredProducts.map((product: any) => (
              <ProductCard key={product.id} product={product} />
            ))
          )}
        </div>

        <div className="text-center mt-12">
          <Link to="/shop" onClick={clearShopScrollState}>
            <Button className="btn-secondary">
              {t('shop')}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* New Arrivals Section */}
      {newArrivals.length > 0 && (
        <section className="section-container">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-serif font-medium text-foreground mb-4">
              {language === 'sv' && 'Nykomlingar'}
              {language === 'en' && 'New Arrivals'}
              {language === 'ar' && 'وصل حديثا'}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {language === 'sv' && 'Upptäck våra senaste produkter'}
              {language === 'en' && 'Discover our latest products'}
              {language === 'ar' && 'اكتشف أحدث منتجاتنا'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {loading ? (
              <p className="text-center col-span-full">Loading...</p>
            ) : (
              newArrivals.map((product: any) => (
                <ProductCard key={product.id} product={product} />
              ))
            )}
          </div>

          <div className="text-center mt-12">
            <Link to="/shop" onClick={clearShopScrollState}>
              <Button className="btn-secondary">
                {t('shop')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </section>
      )}

      {/* Why Organic Section */}
      <section className="section-container">
        <div className="max-w-4xl mx-auto text-center space-y-6">
          <h2 className="text-3xl md:text-4xl font-serif font-medium text-foreground">
            {t('whyOrganic')}
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            {t('organicBenefits')}
          </p>

          <div className="grid md:grid-cols-3 gap-8 mt-12">
            <div className="space-y-3 p-6 rounded-lg bg-card shadow-soft">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                <span className="text-2xl">🌿</span>
              </div>
              <h3 className="font-serif text-xl font-medium">100% Natural</h3>
              <p className="text-sm text-muted-foreground">
                Pure ingredients from nature, no synthetic additives
              </p>
            </div>

            <div className="space-y-3 p-6 rounded-lg bg-card shadow-soft">
              <div className="w-12 h-12 rounded-full bg-secondary/10 flex items-center justify-center mx-auto">
                <span className="text-2xl">🤲</span>
              </div>
              <h3 className="font-serif text-xl font-medium">Handcrafted</h3>
              <p className="text-sm text-muted-foreground">
                Traditional methods passed down through generations
              </p>
            </div>

            <div className="space-y-3 p-6 rounded-lg bg-card shadow-soft">
              <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center mx-auto">
                <span className="text-2xl">🌍</span>
              </div>
              <h3 className="font-serif text-xl font-medium">Sustainable</h3>
              <p className="text-sm text-muted-foreground">
                Eco-friendly production and packaging
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Product Guide Assistant - Lazy loaded */}
      {isAssistantOpen && (
        <Suspense fallback={null}>
          <ProductGuideAssistant 
            isOpen={isAssistantOpen}
            onClose={() => setIsAssistantOpen(false)}
          />
        </Suspense>
      )}
    </div>
  );
};

export default Home;
